import { PageSkeleton } from '@kc-console/common-ui';
import { DETAIL_REDIRECT_STATUS_CODES, getHttpStatus, STATUS_CODE } from '@kc-console/core';
import { InServiceTemporaryError } from '@kc-console/shared-ui';

import { InServiceErrorLayout } from '../ErrorLayout/InServiceErrorLayout';
import { usePanelErrorBoundaryContext } from '../PanelErrorBoundary/PanelErrorBoundaryContext';
import PanelErrorFallback from '../PanelErrorBoundary/PanelErrorFallback';
import useDetailErrorRedirect from './hooks/useDetailErrorRedirect';

import type { PropsWithChildren, ReactNode } from 'react';
import type {
  DetailErrorMessage,
  UseDetailErrorRedirectParams,
} from './hooks/useDetailErrorRedirect';

export interface DetailContentGateRedirectOptions
  extends Pick<
    UseDetailErrorRedirectParams,
    'replace' | 'shouldHandle' | 'onBeforeRedirect' | 'toastOptions'
  > {
  // 게이팅 에러 발생 시 이동할 목록/상위 페이지 경로입니다.
  to: string;
  // 생략하면 기본 상세 로드 실패 문구를 사용합니다.
  message?: DetailErrorMessage;
}

export interface DetailContentGateProps extends PropsWithChildren {
  // 게이팅(화면 렌딩 결정) 조회의 원본 에러입니다. 상태 코드 판별(401 제외, 패널 403 화면 분기)에 사용됩니다.
  error?: unknown;
  // 로딩/에러/빈 데이터 상태에서 보여줄 대체 UI입니다. 기본값은 <PageSkeleton variant="detail" />입니다.
  fallback?: ReactNode;
  // 기본값은 false입니다. 게이팅 조회가 마지막 성공 데이터(children 렌더 가능)를 보유 중인지 여부입니다.
  // "종착(리다이렉트/모달)이 커밋되는 동안 현재 화면(children)을 유지할지, 스켈레톤을 보여줄지"를 결정합니다.
  //   - 403/404 목록 복귀: hasData면 현재 상세 화면을 유지한 채 이동(정상 렌딩 후 폴링 에러), 아니면 스켈레톤(초기 진입).
  //   - 401(전역 Error401Modal 소유): hasData면 마지막 데이터 유지, 아니면 스켈레톤.
  //   - 5xx·network·null 일시 오류는 상세 렌더가 불가하므로 hasData와 무관하게 에러 화면으로 교체합니다.
  // (종착 여부 자체는 hasData가 아니라 에러 종류로만 결정합니다. 종착 시 반드시 이동/에러화면으로 넘어가므로
  // 삭제된 리소스의 stale 데이터가 영구히 남지 않습니다.)
  hasData?: boolean;
  // API는 성공했지만 상세 데이터가 없는 상태를 fallback으로 처리할 때 사용합니다.
  // 주의(비종착): isEmpty는 redirect/에러 화면 없이 fallback(스켈레톤)을 영구 유지합니다. 따라서 "정상 200이지만
  // 데이터가 비는" 표면(예: 목록 조회로 단건을 찾는 content[0] 패턴)에서 미존재 리소스를 isEmpty로 처리하면
  // 영구 스켈레톤이 됩니다. 미존재를 4xx로 받는 표면에서만 isEmpty가 안전하며, 200+empty가 가능한 표면은
  // isEmpty 대신 redirect 트리거(또는 빈 상태 UI)를 고려하세요.
  isEmpty?: boolean;
  // 기본값은 false입니다. 게이팅 조회가 실패한 상태인지 여부입니다.
  isError?: boolean;
  // API가 403(권한 없음)을 HTTP status가 아닌 본문 코드로만 표현하는 표면에서 사용합니다(예: KMS body-code 4104).
  // 403과 동일하게 종착합니다(페이지=목록 복귀, 패널=권한 없음 화면). HTTP status 없는 합성 에러를
  // isError에 주입하지 말고 이 prop으로 권한 없음을 표현하세요(status=null "그 외 에러=일시 오류 페이지"와 구분).
  // isNotFound(404 계열)와 대칭인 forbidden 계열 탈출구입니다.
  isForbidden?: boolean;
  // 기본값은 false입니다. true이면 fallback을 렌더링합니다.
  isLoading?: boolean;
  // API가 200 + 빈 데이터로 미존재를 표현하는 표면(목록 조회의 content[0] 패턴)에서 사용합니다.
  // 404와 동일하게 종착합니다(페이지=목록 복귀, 패널=not-found 화면). HTTP status 없는 합성 에러를
  // isError에 주입하지 말고 이 prop으로 미존재를 표현하세요(status=null "그 외 에러=일시 오류 페이지"와 구분).
  isNotFound?: boolean;
  // 값이 있으면 "페이지 종착"으로 동작합니다(에러 시 토스트 + 목록 리다이렉트, 스켈레톤 유지).
  // 값이 없으면 "패널 종착"으로 동작합니다(패널 내부 에러 화면으로 교체).
  redirect?: DetailContentGateRedirectOptions;
}

/**
 * 상세 콘텐츠의 게이팅(화면 렌딩 결정) 비동기 상태를 공통 처리하는 경계 컴포넌트입니다.
 *
 * @description
 * - "게이팅 API" 1개의 상태(isLoading/isError/isEmpty)만 이 게이트에 전달합니다. 상세 영역을 구성하는
 *   추가(보조) API의 실패는 각 섹션이 인라인으로 graceful 처리하며 이 게이트를 거치지 않습니다.
 * - 종착 여부/방식은 에러 종류로만 결정합니다(폴링/초기 로드/데이터 보유는 판정에 넣지 않습니다). hasData는 종착
 *   여부가 아니라 "종착 커밋 중 현재 화면(children) 유지 vs 스켈레톤"이라는 표시(transient)만 결정합니다.
 *   - 403/404(및 body-code fallback: isForbidden=403 · isNotFound=404/200+empty 미존재): "목록 복귀"입니다.
 *     - 페이지 종착(redirect 지정): 토스트 + 목록/상위 페이지로 replace. 처리 중에는 정상 렌딩 후(hasData)면 현재
 *       상세 화면을, 초기 진입(데이터 없음)이면 스켈레톤을 유지합니다.
 *     - 패널 종착(redirect 미지정): 403=권한 없음 · 404=not-found 인플레이스 에러 화면으로 교체합니다.
 *   - 그 외(500·429·503·네트워크·타임아웃·status=null): "일시 오류"입니다.
 *     - 페이지 종착: 상세 렌더가 불가하므로 InServiceTemporaryError 전체 페이지로 교체합니다(토스트/리다이렉트 없음).
 *     - 패널 종착: 일시 오류 EmptyState로 교체합니다.
 *     (패널은 목록으로 리다이렉트할 수 없고, 조회 실패는 예상 가능한 상태라 렌더 예외로 던지지 않습니다.)
 * - 401은 전역 Error401Modal(로그아웃 리다이렉트)이 소유하므로 두 종착 모두 401에서는 종착을 실행하지 않고
 *   마지막 데이터(hasData)/스켈레톤(fallback)을 유지합니다. (전역 모달은 axios onRejected가 transport 401을
 *   4011로 정규화해 띄우는 기본 동작에 의존하므로, 게이팅 쿼리는 기본 onRejected를 사용해야 합니다.
 *   getShouldRedirectUnauthorized를 커스텀해 401 정규화를 우회하는 인스턴스는 게이트 표면에서 사용하지 않습니다.)
 * - 상태 코드 판별(401 제외, 403/404 목록 복귀, 패널 403/404 화면 분기)은 getHttpStatus에 의존하며 이는 axios
 *   에러에서만 status를 추출합니다. 따라서 게이팅 쿼리는 axios 기반이어야 합니다. 비-axios 에러는 status=null로
 *   간주되어 "그 외 에러(일시 오류)"로 처리됩니다. 200+empty 미존재는 합성 에러 대신 isNotFound prop으로,
 *   HTTP status로 오지 않는 권한 없음(본문 코드 전용)은 isForbidden prop으로 표현하세요.
 * - 종착 우선순위: 종착 에러(비401)가 isLoading/isEmpty보다 우선입니다. 로딩과 에러가 동시인 refetch 상황에서도
 *   에러 화면(패널)·일시 오류 페이지(페이지 종착 5xx)·리다이렉트(페이지 종착 403/404)가 fallback보다 우선합니다.
 * - hasData(마지막 성공 데이터 보유)는 "종착 커밋 중 현재 화면 유지 vs 스켈레톤"을 결정합니다.
 *   - 403/404 목록 복귀: hasData이면 현재 상세 화면(children)을 유지한 채 토스트+리다이렉트(정상 렌딩 후 폴링 에러),
 *     초기 진입(데이터 없음)이면 스켈레톤. 리다이렉트가 곧바로 커밋되므로 stale children이 영구히 남지 않습니다.
 *   - 401: hasData이면 마지막 데이터(children)를, 초기 로드이면 스켈레톤을 유지합니다.
 *   - 5xx·network·null: 상세 렌더가 불가하므로 hasData와 무관하게 일시 오류 화면으로 교체합니다.
 *   (종착 여부 자체는 에러 종류로만 결정하고 반드시 이동/에러화면으로 넘어가므로, 삭제된 리소스를 폴링 중 감지하지
 *   못하고 stale 데이터를 계속 보여주던 회귀는 발생하지 않습니다.)
 * - redirect.shouldHandle로 종착 대상을 좁히는 호출부는 403/404만 필터 대상으로 삼으세요. shouldHandle이
 *   403/404를 걸러내면 리다이렉트도 일시 오류 페이지도 없이 fallback(스켈레톤)에 갇힐 수 있습니다.
 * - 게이팅 소스로는 placeholderData: keepPreviousData 쿼리를 쓰지 않습니다. 항목 전환 시 이전 데이터가
 *   유지되면 새 항목의 로딩 프레임에서 이전 항목의 stale 콘텐츠가 새 항목 헤더와 함께 노출되는 회귀가 생깁니다.
 *
 * @default
 * - isLoading: false
 * - isError: false
 * - isNotFound: false
 * - isForbidden: false
 * - hasData: false
 * - isEmpty: false
 * - fallback: <PageSkeleton variant="detail" />
 * - redirect: undefined (패널 종착 — 패널 에러 화면 렌더)
 *
 * @example 페이지 종착 (목록으로 토스트 + 리다이렉트)
 * ```tsx
 * <DetailContentGate
 *   isLoading={isLoading}
 *   isError={isError}
 *   error={error}
 *   isEmpty={!cluster}
 *   redirect={{ to: getRouterPath(ROUTE_PATH.CLUSTER_LIST) }}
 * >
 *   <DetailLayout>{children}</DetailLayout>
 * </DetailContentGate>
 * ```
 *
 * @example 패널 종착 (인플레이스 에러 화면)
 * ```tsx
 * <DetailContentGate isLoading={isLoading} isError={isError} error={error} isEmpty={!applicationInfo}>
 *   <PanelLayout>{children}</PanelLayout>
 * </DetailContentGate>
 * ```
 */
export default function DetailContentGate({
  children,
  isLoading = false,
  isError = false,
  isNotFound = false,
  isForbidden = false,
  error,
  hasData = false,
  isEmpty = false,
  fallback = <PageSkeleton variant="detail" />,
  redirect,
}: DetailContentGateProps) {
  const { onClose } = usePanelErrorBoundaryContext();
  const isPageTerminal = !!redirect;
  const httpStatus = getHttpStatus(error);
  // 401은 전역 Error401Modal이 소유하므로 어느 종착도 실행하지 않는다(마지막 데이터/스켈레톤 유지).
  const isUnauthorized = httpStatus === STATUS_CODE.UNAUTHORIZED;
  // 401을 제외한 게이팅 에러는 데이터 보유·폴링 여부와 무관하게 종착한다(삭제 후 stale 유지 회귀 제거).
  // isNotFound(404 계열)·isForbidden(403 계열)은 HTTP status 없이도 종착을 강제하는 body-code 탈출구다.
  const shouldTerminate = (isError || isNotFound || isForbidden) && !isUnauthorized;
  // 403/404(및 body-code fallback: isForbidden=403·isNotFound=404/200+empty)만 목록 복귀.
  // 그 외(5xx·network·null)는 일시 오류로 처리한다.
  const isMissingOrForbidden =
    isNotFound ||
    isForbidden ||
    (httpStatus !== null && DETAIL_REDIRECT_STATUS_CODES.includes(httpStatus));
  const shouldRedirect = shouldTerminate && isMissingOrForbidden;

  // Hook 호출 순서를 보장하기 위해 redirect가 없어도 항상 호출하고 enabled로 제어합니다.
  // (패널 종착에서는 enabled: false로 토스트/라우팅을 모두 비활성화합니다.)
  // 목록 복귀 대상(403/404·isNotFound)만 페이지 종착 훅에 위임하고, 그 외 에러는 아래 일시 오류 페이지가 담당합니다.
  useDetailErrorRedirect({
    isError: shouldRedirect,
    error,
    enabled: isPageTerminal,
    redirectTo: redirect?.to ?? '',
    message: redirect?.message,
    replace: redirect?.replace,
    shouldHandle: redirect?.shouldHandle,
    onBeforeRedirect: redirect?.onBeforeRedirect,
    toastOptions: redirect?.toastOptions,
  });

  // 패널 종착: 조회 실패는 예상 가능한 비동기 상태이므로 렌더 예외로 던지지 않고 에러 화면을 직접 렌더합니다.
  // ErrorBoundary를 통한 throw는 개발 오버레이/콘솔 에러를 유발하므로, 실제 렌더 예외 처리와 상태 분기를 분리합니다.
  // axios onRejected가 에러를 spread({ ...error })로 재구성해도 response.status는 보존되므로
  // PanelErrorFallback이 getHttpStatus로 403(권한 없음)/404(not-found) 화면을 분기할 수 있습니다.
  if (!isPageTerminal && shouldTerminate) {
    return (
      <PanelErrorFallback
        error={error}
        isNotFound={isNotFound}
        isForbidden={isForbidden}
        onClose={onClose}
      />
    );
  }

  // 페이지 종착 · 비리다이렉트 에러(5xx·network·null): 상세 렌더가 불가하므로 일시 오류 전체 페이지로 교체합니다.
  // (재시도 버튼은 노출하지 않습니다 — 시안대로 hideButton. 수동 복구 경로는 새로고침/포커스 복귀입니다.)
  if (isPageTerminal && shouldTerminate && !isMissingOrForbidden) {
    return (
      <InServiceErrorLayout>
        <InServiceTemporaryError hideButton />
      </InServiceErrorLayout>
    );
  }

  // 페이지 종착의 목록 복귀(403/404·isNotFound): 토스트 + 리다이렉트가 커밋되는 동안 어떤 화면을 유지할지 결정합니다.
  // - 정상 렌딩 후 폴링/포커스 refetch에서 에러가 온 경우(hasData): 현재 상세 화면을 그대로 유지한 채 이동합니다
  //   (스켈레톤으로 깜빡이지 않고 "현재 화면 → 토스트 → 목록"으로 자연스럽게 넘어갑니다).
  // - 초기 진입에서 에러가 온 경우(데이터 없음): 보여줄 콘텐츠가 없으므로 스켈레톤을 유지합니다.
  // 두 경우 모두 리다이렉트가 곧바로 커밋되므로 stale children이 영구히 남지 않으며, hasData는 항상 마지막 성공
  // 데이터에서 파생됩니다(배경 refetch 에러 시 React Query가 data를 보존). keepPreviousData 게이팅 소스 금지와
  // 맞물려 이전 항목의 stale 데이터가 새 항목 화면으로 새지 않습니다.
  if (shouldRedirect) {
    return <>{hasData ? children : fallback}</>;
  }

  // 게이팅 상태가 통과되기 전까지는 동일한 fallback을 유지합니다.
  if (isLoading || isEmpty) {
    return <>{fallback}</>;
  }

  // 401: 전역 모달이 인증 만료를 안내하므로 마지막 데이터를 유지합니다. 초기 로드면 보여줄 게 없어 스켈레톤을 유지합니다.
  if (isError && isUnauthorized && !hasData) {
    return <>{fallback}</>;
  }

  // 모든 게이팅 상태가 통과된 뒤에만 실제 상세 콘텐츠를 렌더링합니다.
  return <>{children}</>;
}
